import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PersonalDashboard } from '@/components';
import { CoachDashboard } from '@/components/coach-dashboard';
import { getApiUrl } from '@/config/api';

/**
 * UnifiedDashboard - Intelligent dashboard router
 * 
 * This component determines which dashboard view to show based on:
 * - User role (athlete, coach, admin)
 * - Wearable connection status
 * 
 * Behavior:
 * - Athletes: Always see PersonalDashboard with their own data
 * - Coaches without wearable: See CoachDashboard with team data only
 * - Coaches with wearable: See CoachDashboard with their data + team data
 *   (their data automatically appears in athlete lists/rankings via backend)
 */
const UnifiedDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [userInfo, setUserInfo] = useState<{
        role?: string;
        whoop_connected?: boolean;
        username?: string;
        email?: string;
    } | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Check authentication
        const token = localStorage.getItem('access');
        if (!token) {
            navigate('/login');
            return;
        }

        // Get user info from localStorage (already fetched during login)
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                const user = JSON.parse(userInfoString);
                setUserInfo(user);
                setIsLoading(false);
            } catch (err) {
                console.error('Error parsing user info:', err);
                // Fallback: fetch user info from API
                fetchUserInfo(token);
            }
        } else {
            // Fetch user info from API if not in localStorage
            fetchUserInfo(token);
        }
    }, [navigate]);

    const fetchUserInfo = async (token: string) => {
        try {
            const response = await fetch(getApiUrl('/api/users/me/'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const user = await response.json();
                setUserInfo(user);
                localStorage.setItem('user_info', JSON.stringify(user));
            } else {
                console.error('Failed to fetch user info');
                navigate('/login');
            }
        } catch (err) {
            console.error('Error fetching user info:', err);
            navigate('/login');
        } finally {
            setIsLoading(false);
        }
    };

    // Show loading state
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--spinner-color)] mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    // Determine which dashboard to show based on user role
    const userRole = userInfo?.role;
    
    // Athletes always see their personal dashboard
    if (userRole === 'athlete') {
        return <PersonalDashboard />;
    }

    // Coaches and admins see the coach dashboard
    // If they have a wearable connected, they'll automatically appear in athlete data
    if (userRole === 'coach' || userRole === 'admin') {
        return <CoachDashboard />;
    }

    // Fallback: show personal dashboard for unknown roles
    console.warn('UnifiedDashboard: Unknown role, showing PersonalDashboard. Role:', userRole);
    return <PersonalDashboard />;
};

export default UnifiedDashboard;

